import type { Stripe } from "@stripe/stripe-js";
import { ProductOrderActionsType } from "../components/product/selectProductOrder";
import { log, updateCart } from "./api";
import { Product, ProductVariation } from "./types";
import { loadStripe } from "@stripe/stripe-js/pure";
import { IGatsbyImageData } from "gatsby-plugin-image";
import { SVG } from "@svgdotjs/svg.js"; // registerWindow
import CryptoJS from "crypto-js";
import { fastUpdateProductQuantity } from "./store";
// import { createSVGWindow } from "svgdom";

// TEST
// export const stripeLoader: () => Promise<Stripe | null> = () =>
//   loadStripe(
//     "pk_test_51NNy06Kjw5xZIMAqgFQ6gEQ4yp1mc4VLh0nXZwlOS7rDh1Kh74bku53vWP5QgqPchkSBC8txQrOgt5VxGTHpkLkx00pFu3zMlV"
//   );

// LIVE
export const stripeLoader: () => Promise<Stripe | null> = () =>
  loadStripe(process.env.GATSBY_STRIPE);

export const formatPrice: (price: number) => string = (price: number) =>
  String((price / 100).toFixed(2));

export const getFormattedDate: (date: Date) => string = (date) =>
  date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

export const decodeHtmlCharCodes = (str: string): string => {
  return str.replace(/(&#(\d+);)/g, function (match, capture, charCode) {
    return String.fromCharCode(charCode);
  });
};

export const productsReducer: (
  products: Product[],
  action: ProductOrderActionsType
) => Product[] = (products: Product[], action: ProductOrderActionsType) => {
  switch (action) {
    case "rating":
      return [
        ...products.sort(
          (a, b) => Number(b.average_rating) - Number(a.average_rating)
        ),
      ];
    case "popularity":
      return [...products.sort((a, b) => b.rating_count - a.rating_count)];
    case "latest":
      return [
        ...products.sort(
          (a, b) =>
            new Date(b.date_created).getTime() -
            new Date(a.date_created).getTime()
        ),
      ];
    case "low_to_high":
      return [...products.sort((a, b) => Number(a.price) - Number(b.price))];
    case "high_to_low":
      return [...products.sort((a, b) => Number(b.price) - Number(a.price))];
    default:
      return products;
  }
};

type GetVariationByColorType = (
  productVariations: ProductVariation[]
) => Record<string, ProductVariation[]>;

export const getVariationByColor: GetVariationByColorType = (
  productVariations
) => {
  return productVariations.reduce<Record<string, ProductVariation[]>>(
    (acc, val) => {
      const color = val.attributes2[0].value;
      if (!acc[color]) {
        acc[color] = [];
      }

      acc[color].push(val);
      return acc;
    },
    {}
  );
};

export const updateProductQuantity: (
  key: string,
  quantity: number
) => Promise<void> = async (key, quantity) => {
  const body = {
    cart: {
      [key]: quantity,
    },
  };
  fastUpdateProductQuantity(key, quantity);

  await updateCart(body);
};

export const getAddressData = (
  target: EventTarget & HTMLFormElement,
  prefix_option: "billing" | "shipping",
  prefix_form: "billing" | "shipping"
) => {
  const addressData = {
    [`${prefix_option}_first_name`]: target[`${prefix_form}_first_name`].value,
    [`${prefix_option}_last_name`]: target[`${prefix_form}_last_name`].value,
    // [`${prefix_option}_email`]: target[`billing_email`].value,
    [`${prefix_option}_phone`]: target[`${prefix_form}_phone`].value,
    [`${prefix_option}_country`]: target[`${prefix_form}_country`].value,
    // [`${prefix_option}_state`]: target[`${prefix_form}_state`].value,
    [`${prefix_option}_city`]: target[`${prefix_form}_city`].value,
    [`${prefix_option}_address_1`]: target[`${prefix_form}_address_1`].value,
    [`${prefix_option}_address_2`]: target[`${prefix_form}_address_2`].value,
    [`${prefix_option}_postcode`]: target[`${prefix_form}_postcode`].value,
  };

  const potentialStateField = target[`${prefix_form}_state`];
  if (potentialStateField) {
    addressData[`${prefix_option}_state`] = potentialStateField.value;
  }
  return addressData;
};

// In case we use the same billing address, copy it from the Shipping Info store
export const copyShippingToBilling = (
  shippingInfo: any,
  billingAddress: any
) => {
  const result = { ...billingAddress };

  for (const [key, value] of Object.entries(shippingInfo)) {
    if (key in ["order_comments", "billing_email"]) continue;

    const newKey = key.replace("shipping", "billing");
    result[newKey] = value;
  }

  return result;
};

export const getGatsbyImageData: (image: any) => IGatsbyImageData | null = (
  image
) => {
  return image?.childrenImageSharp?.length
    ? image?.childrenImageSharp[0]?.gatsbyImageData
    : null;
};

// Color button SVG generation
type PolarToCartesianOptions = {
  centerX: number;
  centerY: number;
  radius: number;
  angleInDegrees: number;
};

const polarToCartesian = (options: PolarToCartesianOptions) => {
  const { centerX, centerY, radius, angleInDegrees } = options;
  const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0;

  return {
    x: centerX + radius * Math.cos(angleInRadians),
    y: centerY + radius * Math.sin(angleInRadians),
  };
};

type DescribeArcOptions = {
  x: number;
  y: number;
  radius: number;
  startAngle: number;
  endAngle: number;
};

const describeArc = (options: DescribeArcOptions) => {
  const { x, y, radius, startAngle, endAngle } = options;

  const start = polarToCartesian({
    centerX: x,
    centerY: y,
    radius,
    angleInDegrees: endAngle,
  });
  const end = polarToCartesian({
    centerX: x,
    centerY: y,
    radius,
    angleInDegrees: startAngle,
  });

  const arcSweep = endAngle - startAngle <= 180 ? "0" : "1";

  // prettier-ignore
  const d = [
    "M", start.x, start.y,
    "A", radius, radius, 0, arcSweep, 0, end.x, end.y,
    "L", x, y,
    "L", start.x, start.y
  ].join(" ");

  return d;
};

type CircleOptions = {
  x: number;
  y: number;
  radius: number;
  colors: string[];
};

export const createMultiColorCircle = (options: CircleOptions): string => {
  const { x, y, radius, colors } = options;

  // // returns a window with a document and an svg root node
  // const window = createSVGWindow();
  // const document = window.document;

  // // register window and document
  // registerWindow(window, document);

  // Create an SVG canvas
  const canvas = SVG().size("100%", "100%");

  // Create a group for the pie chart
  const pieGroup = canvas.group();

  // Initialize the starting angle
  let startAngle = 0;

  // Draw the pie slices
  for (let i = 0; i < colors.length; i++) {
    const percentage = (1 / colors.length) * 100;
    const endAngle = startAngle + (360 * percentage) / 100;

    // Calculate the arc path for the slice
    const arcPath = describeArc({ x, y, radius, startAngle, endAngle });

    // Create a path element for the slice
    pieGroup.path(arcPath).fill(colors[i]);

    // Update the starting angle for the next slice
    startAngle = endAngle;
  }

  // Convert the SVG to a string
  const svgString = canvas.svg();

  return svgString;
};

export const isTestPayment = () => {
  return document.cookie.includes('test_payment=1');
};

export const hashEmail = (email: string | null): string | null => {
  if (!email) return null;
  return CryptoJS.SHA256(email).toString();
}

export const decodeHTMLEntities = (str: string) => {
  const entities = {
    '&amp;': '&',
    '&apos;': "'",
    '&lt;': '<',
    '&gt;': '>',
    '&quot;': '"',
    '&#039;': "'",
    '&#038;': '&',
  };

  return str.replace(/&[#A-Za-z0-9]+;/g, entity => entities[entity] || entity);
}

export const gtag = (type: string, source: string, data: any = {}) => {
  if (typeof window !== "undefined" && !!window?.gtag) {
    if (data) {
      window.gtag(type, source, data);
    } else {
      window.gtag(type, source);
    }
    log('gtag', `${type}-${source}`, data);
  }
}

export const cartQueueManager = (() => {
  let queue = Promise.resolve();
  let isProcessing = false;

  const addToQueue = async (task) => {
    queue = queue.then(async () => {
      if (!isProcessing) {
        isProcessing = true;
        try {
          await task();
        } finally {
          isProcessing = false;
        }
      }
    });
    return queue;
  };

  return { addToQueue };
})();
